import * as React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * 렌더 중 예외를 잡아 앱 전체가 흰 화면이 되는 것을 막는다.
 *
 * 왜 필요한가:
 * 이 앱에는 렌더 도중 터질 수 있는 지점이 여럿 있다(달력의 capacityClassItem,
 * 본인인증 콜백 파싱, 미지원 연도의 공휴일 데이터 등). React 는 경계가 없으면
 * 예외가 난 순간 **트리 전체를 언마운트**하므로 하단 탭까지 사라진다.
 * 모바일에는 콘솔이 없어 사용자에게는 이유 없는 백지만 남고, 앱을 강제 종료하는
 * 것 외에 빠져나갈 방법이 없다.
 *
 * 한계(중요): 에러 경계는 **렌더 · 생명주기 · 생성자**의 예외만 잡는다.
 * 이벤트 핸들러, setTimeout, Promise 안에서 난 예외는 잡지 못하므로
 * 그쪽은 각 호출부의 try/catch · .catch 로 계속 처리해야 한다.
 */

type Props = {
  children: React.ReactNode;
  /**
   * 이 값이 바뀌면 오류 상태를 푼다.
   *
   * 없으면 한 번 터진 뒤로는 화면을 옮겨도 계속 오류 화면이 남는다.
   * 안드로이드 뒤로가기로 정상 화면에 돌아왔는데도 오류 화면이 그대로인 상황을
   * 막기 위해 경로를 키로 넣는다(아래 RouteErrorBoundary).
   */
  resetKey?: string;
};

type State = { hasError: boolean };

const wrapStyle: React.CSSProperties = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  background: '#F1F2F4',
  color: '#555',
  fontFamily: 'inherit',
};

const titleStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: '18px',
  fontWeight: 700,
  color: '#333',
};

const messageStyle: React.CSSProperties = {
  margin: '0 0 28px',
  fontSize: '14px',
  lineHeight: 1.7,
  whiteSpace: 'pre-line',
  // index.html 의 `* { user-select: none }` 때문에 기본값으로는 복사가 안 된다.
  // 문의할 때 화면 문구를 옮겨 적을 수 있어야 한다.
  userSelect: 'text',
  WebkitUserSelect: 'text',
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '10px',
  flexWrap: 'wrap',
  justifyContent: 'center',
};

const buttonStyle: React.CSSProperties = {
  minWidth: '120px',
  padding: '11px 20px',
  fontSize: '15px',
  fontWeight: 700,
  borderRadius: '10px',
  border: '1px solid #EF8322',
  background: '#EF8322',
  color: '#fff',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const subButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: '#fff',
  color: '#EF8322',
};

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // 모바일에서는 볼 수 없지만, 원격 디버깅·웹 확인 시 원인을 남긴다.
    console.error('[화면 오류]', error, info && info.componentStack);
  }

  componentDidUpdate(prevProps: Props) {
    // 경로가 바뀌면(뒤로가기 포함) 다시 그려 볼 기회를 준다.
    // 같은 화면이 또 터지면 getDerivedStateFromError 가 다시 오류 상태로 만들 뿐,
    // resetKey 가 그대로라 여기서 다시 풀지 않는다 — 무한 반복이 생기지 않는다.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // 이 화면 자체가 또 터지면 의미가 없으므로 라이브러리를 쓰지 않고
    // 기본 태그 + 인라인 스타일로만 그린다.
    return (
      <div style={wrapStyle}>
        <p style={titleStyle}>화면을 표시하지 못했습니다</p>
        <p style={messageStyle}>
          {'일시적인 문제일 수 있습니다.\n다시 시도하거나 홈으로 이동해 주세요.'}
        </p>
        <div style={buttonRowStyle}>
          <button type="button" style={buttonStyle} onClick={() => window.location.reload()}>
            다시 시도
          </button>
          <button type="button" style={subButtonStyle} onClick={() => window.location.assign('/')}>
            홈으로
          </button>
        </div>
      </div>
    );
  }
}

/**
 * 라우터 안에서 쓰는 경계. 경로가 바뀌면 오류 상태를 자동으로 푼다.
 * `<Router>` 안쪽에서만 쓸 수 있다(useLocation 사용).
 */
export const RouteErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname + location.search}>{children}</ErrorBoundary>;
};

export default ErrorBoundary;
